import { useState } from 'react'
import { alsDatum, alsDauer, api } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'

interface Kursvergleich {
  kursnummer: string
  aenderung: 'NEU' | 'ENTFAELLT' | 'GEAENDERT' | 'UNVERAENDERT'
  alteFassungAb: string | null
  neueFassungAb: string | null
  alteZeiten: string | null
  neueZeiten: string | null
  alteArbeitszeit: number | null
  neueArbeitszeit: number | null
  alteTage: string[]
  neueTage: string[]
  teamZugeordnet: boolean
}

interface BetroffeneZuteilung {
  betriebstag: string
  kursnummer: string
  fahrer: string | null
  fahrzeug: string | null
  grund: string
}

interface Vorschau {
  stichtag: string
  vergleichstag: string
  neu: number
  entfaellt: number
  geaendert: number
  unveraendert: number
  kurse: Kursvergleich[]
  betroffeneZuteilungen: BetroffeneZuteilung[]
  kurseOhneTeam: string[]
}

const ETIKETT: Record<string, string> = {
  NEU: 'gruen',
  ENTFAELLT: 'rot',
  GEAENDERT: 'orange',
  UNVERAENDERT: 'grau',
}

const TEXT: Record<string, string> = {
  NEU: 'neu',
  ENTFAELLT: 'entfällt',
  GEAENDERT: 'geändert',
  UNVERAENDERT: 'unverändert',
}

const TAG_KURZ: Record<string, string> = {
  MONDAY: 'Mo',
  TUESDAY: 'Di',
  WEDNESDAY: 'Mi',
  THURSDAY: 'Do',
  FRIDAY: 'Fr',
  SATURDAY: 'Sa',
  SUNDAY: 'So',
}

/**
 * Vergleicht den Fahrplanstand vor und nach einem Stichtag.
 *
 * Es wird nichts „aktiviert": Kurse gelten allein über ihr Gültigkeitsdatum. Der Stichtag
 * ist ein Datum, keine Schaltung — deshalb kann auch nichts vergessen werden.
 */
export function Fahrplanwechsel() {
  const { hatRolle } = useAuth()
  const darfUmhaengen = hatRolle('ADMIN', 'DISPONENT')

  const [stichtag, setStichtag] = useState('')
  const [vorschau, setVorschau] = useState<Vorschau | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [nurAenderungen, setNurAenderungen] = useState(true)

  async function pruefen() {
    if (!stichtag) return
    setLaeuft(true)
    setMeldung(null)
    try {
      setVorschau(await api.get<Vorschau>(`/api/disposition/fahrplanwechsel?stichtag=${stichtag}`))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Prüfung fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  async function umhaengen() {
    if (!stichtag) return
    setLaeuft(true)
    try {
      const e = await api.post<{ umgehaengt: number }>(
        `/api/disposition/fahrplanwechsel/umhaengen?stichtag=${stichtag}`,
      )
      setMeldung({
        art: 'erfolg',
        text: `${e.umgehaengt} Besetzung(en) auf die neue Fassung umgehängt.`,
      })
      await pruefen()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Umhängen fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  const sichtbar = vorschau?.kurse.filter((k) =>
    nurAenderungen ? k.aenderung !== 'UNVERAENDERT' : true,
  )

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Fahrplanwechsel</h1>
          <p>
            Was ändert sich zum Stichtag? Der neue Fahrplan gilt automatisch ab seinem
            Gültigkeitsdatum — hier sehen Sie vorab, was auf Sie zukommt.
          </p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      <div className="werkzeugleiste">
        <div className="feld" style={{ marginBottom: 0 }}>
          <label htmlFor="stichtag">Stichtag des Wechsels</label>
          <input
            id="stichtag"
            type="date"
            value={stichtag}
            onChange={(e) => setStichtag(e.target.value)}
          />
        </div>
        <button
          className="knopf-primaer"
          disabled={!stichtag || laeuft}
          onClick={() => void pruefen()}
          style={{ alignSelf: 'flex-end' }}
        >
          {laeuft ? 'Wird geprüft …' : 'Vergleichen'}
        </button>
      </div>

      {vorschau && (
        <>
          <div className="karten-raster">
            <div className={`kennzahl ${vorschau.neu > 0 ? '' : 'navy'}`}>
              <div className="wert">{vorschau.neu}</div>
              <div className="beschriftung">neue Kurse</div>
            </div>
            <div className={`kennzahl ${vorschau.entfaellt > 0 ? 'rot' : 'navy'}`}>
              <div className="wert">{vorschau.entfaellt}</div>
              <div className="beschriftung">entfallende Kurse</div>
            </div>
            <div className={`kennzahl ${vorschau.geaendert > 0 ? 'orange' : 'navy'}`}>
              <div className="wert">{vorschau.geaendert}</div>
              <div className="beschriftung">geänderte Dienstzeiten</div>
            </div>
            <div className="kennzahl navy">
              <div className="wert">{vorschau.unveraendert}</div>
              <div className="beschriftung">unverändert</div>
            </div>
          </div>

          {vorschau.kurseOhneTeam.length > 0 && (
            <div className="meldung hinweis">
              <strong>Ohne Teamzuordnung:</strong> Kurs {vorschau.kurseOhneTeam.join(', ')}.
              Solange kein Team diese Kurse abdeckt, findet die Disposition dafür keinen Fahrer.
              Ordnen Sie sie unter <strong>Teams</strong> zu oder tragen Sie Springer ein.
            </div>
          )}

          {vorschau.betroffeneZuteilungen.length > 0 && (
            <div className="karte abschnitt" style={{ borderLeft: '4px solid var(--orange-500)' }}>
              <h2>
                {vorschau.betroffeneZuteilungen.length} geplante Besetzung(en) sind betroffen
              </h2>
              <p style={{ color: 'var(--grau-700)', fontSize: '0.88rem' }}>
                Diese Zuteilungen liegen nach dem Stichtag und beruhen auf dem alten Fahrplan.
                {darfUmhaengen && ' Beim Umhängen bleiben Fahrer und Fahrzeug erhalten — nur die ' +
                  'zugrunde liegende Kursfassung wird aktualisiert. Besetzungen entfallender Kurse ' +
                  'bleiben unberührt, damit Sie selbst entscheiden, wohin der Fahrer stattdessen kommt.'}
              </p>
              <div className="tabelle-huelle" style={{ boxShadow: 'none', marginTop: 10 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Kurs</th>
                      <th>Fahrer</th>
                      <th>Fahrzeug</th>
                      <th>Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vorschau.betroffeneZuteilungen.map((z, i) => (
                      <tr key={i}>
                        <td className="zahl">{alsDatum(z.betriebstag)}</td>
                        <td>
                          <strong>{z.kursnummer}</strong>
                        </td>
                        <td>{z.fahrer ?? '—'}</td>
                        <td>{z.fahrzeug ?? '—'}</td>
                        <td style={{ color: 'var(--grau-700)', fontSize: '0.85rem' }}>{z.grund}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {darfUmhaengen && (
                <button
                  className="knopf-primaer"
                  style={{ marginTop: 14 }}
                  disabled={laeuft}
                  onClick={() => void umhaengen()}
                >
                  Besetzungen auf die neue Fassung umhängen
                </button>
              )}
            </div>
          )}

          <div className="werkzeugleiste">
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 400, marginBottom: 0 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={nurAenderungen}
                onChange={(e) => setNurAenderungen(e.target.checked)}
              />
              nur Änderungen zeigen
            </label>
            <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
              Vergleich: {alsDatum(vorschau.vergleichstag)} → {alsDatum(vorschau.stichtag)}
            </span>
          </div>

          <div className="tabelle-huelle">
            {sichtbar && sichtbar.length === 0 ? (
              <div className="leer">
                Zum {alsDatum(vorschau.stichtag)} ändert sich nichts. Entweder liegt kein neuer
                Fahrplan vor, oder er beginnt an einem anderen Datum.
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Kurs</th>
                    <th>Änderung</th>
                    <th>Bisher</th>
                    <th>Neu</th>
                    <th>Verkehrstage</th>
                    <th>Arbeitszeit</th>
                    <th>Team</th>
                  </tr>
                </thead>
                <tbody>
                  {sichtbar?.map((k) => (
                    <tr key={k.kursnummer}>
                      <td>
                        <strong>{k.kursnummer}</strong>
                      </td>
                      <td>
                        <span className={`etikett ${ETIKETT[k.aenderung]}`}>
                          {TEXT[k.aenderung]}
                        </span>
                      </td>
                      <td className="zahl">
                        {k.alteZeiten ?? '—'}
                        {k.alteFassungAb && (
                          <div style={{ color: 'var(--grau-500)', fontSize: '0.74rem' }}>
                            ab {alsDatum(k.alteFassungAb)}
                          </div>
                        )}
                      </td>
                      <td className="zahl">
                        {k.neueZeiten ?? '—'}
                        {k.neueFassungAb && (
                          <div style={{ color: 'var(--grau-500)', fontSize: '0.74rem' }}>
                            ab {alsDatum(k.neueFassungAb)}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.83rem' }}>
                        {k.alteTage.join() !== k.neueTage.join() && k.alteTage.length > 0 ? (
                          <>
                            <span style={{ color: 'var(--grau-500)' }}>
                              {k.alteTage.map((t) => TAG_KURZ[t] ?? t).join(' ')}
                            </span>
                            {' → '}
                            <strong>{k.neueTage.map((t) => TAG_KURZ[t] ?? t).join(' ')}</strong>
                          </>
                        ) : (
                          (k.neueTage.length ? k.neueTage : k.alteTage)
                            .map((t) => TAG_KURZ[t] ?? t)
                            .join(' ')
                        )}
                      </td>
                      <td className="zahl">
                        {k.alteArbeitszeit !== k.neueArbeitszeit &&
                        k.alteArbeitszeit !== null &&
                        k.neueArbeitszeit !== null ? (
                          <>
                            <span style={{ color: 'var(--grau-500)' }}>
                              {alsDauer(k.alteArbeitszeit)}
                            </span>
                            {' → '}
                            <strong>{alsDauer(k.neueArbeitszeit)}</strong>
                          </>
                        ) : (
                          alsDauer(k.neueArbeitszeit ?? k.alteArbeitszeit)
                        )}
                      </td>
                      <td>
                        {k.aenderung === 'ENTFAELLT' ? (
                          <span style={{ color: 'var(--grau-500)' }}>—</span>
                        ) : k.teamZugeordnet ? (
                          <span className="etikett gruen">zugeordnet</span>
                        ) : (
                          <span className="etikett orange">fehlt</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!vorschau && !laeuft && (
        <div className="karte">
          <h2>So läuft ein Fahrplanwechsel</h2>
          <ol style={{ lineHeight: 1.9, color: 'var(--grau-700)', paddingLeft: 20 }}>
            <li>
              Neue Kursblätter in der <strong>Kursbibliothek</strong> hochladen. Das Feld
              „gültig ab" im Blatt bestimmt, ab wann sie greifen — nichts davon wirkt vorher.
            </li>
            <li>
              Hier den Stichtag eingeben und vergleichen. Sie sehen neue, entfallende und
              geänderte Kurse sowie betroffene Besetzungen.
            </li>
            <li>
              Neue Kursnummern den <strong>Teams</strong> zuordnen, sonst findet die Disposition
              dafür keine Fahrer.
            </li>
            <li>
              Bereits geplante Besetzungen nach dem Stichtag umhängen.
            </li>
          </ol>
          <p style={{ color: 'var(--grau-500)', fontSize: '0.88rem' }}>
            Am Stichtag selbst ist nichts zu tun: Die Umstellung passiert durch das Datum.
          </p>
        </div>
      )}
    </>
  )
}
